import React, { useState, useEffect } from 'react';
import client from '../api/client';
import { useToast } from '../context/ToastContext';
import { formatDate } from '../utils/dateParser';
import { usePermissions, PAGE_PERMISSION_IDS } from '../hooks/usePermissions';
import { sortCitiesByName } from '../utils/citySearchSelect';

const Cities = () => {
  const { canEdit } = usePermissions();
  const citiesCanEdit = canEdit(PAGE_PERMISSION_IDS.cities);
  const [cities, setCities] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [submitting, setSubmitting] = useState(false); // Защита от двойного клика
  const [deleting, setDeleting] = useState(null); // ID удаляемого города
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    visible_in_schedules: true
  });
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    if (!showForm) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') resetForm();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showForm]);

  const fetchCities = async () => {
    try {
      const response = await client.get('/cities');
      setCities(sortCitiesByName(response.data));
    } catch (error) {
      console.error('Error fetching cities:', error);
      showError('Ошибка при загрузке списка объектов');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return; // Предотвращаем двойной клик
    
    setSubmitting(true);
    try {
      if (editingCity) {
        await client.put(`/cities/${editingCity.id}`, formData);
        showSuccess('Объект строительства обновлён');
      } else {
        await client.post('/cities', formData);
        showSuccess('Объект строительства создан');
      }
      fetchCities();
      resetForm();
    } catch (error) {
      console.error('Error saving city:', error);
      showError('Ошибка при сохранении объекта строительства');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (city) => {
    setEditingCity(city);
    setFormData({
      name: city.name,
      description: city.description || '',
      visible_in_schedules: city.visible_in_schedules !== undefined ? city.visible_in_schedules : true
    });
    setShowForm(true);
  };

  const handleDelete = async (cityId) => {
    if (deleting) return; // Предотвращаем множественные клики
    
    if (window.confirm('Вы уверены, что хотите удалить этот объект строительства?')) {
      setDeleting(cityId);
      try {
        await client.delete(`/cities/${cityId}`);
        showSuccess('Объект строительства удалён');
        fetchCities();
      } catch (error) {
        console.error('Error deleting city:', error);
        showError('Ошибка при удалении объекта строительства');
      } finally {
        setDeleting(null);
      }
    }
  };

  const toggleVisibility = async (city) => {
    try {
      const newVisibility = !city.visible_in_schedules;
      await client.put(`/cities/${city.id}`, {
        name: city.name,
        description: city.description,
        visible_in_schedules: newVisibility
      });
      showSuccess(newVisibility ? 'Объект будет отображаться в графиках' : 'Объект скрыт из графиков');
      fetchCities();
    } catch (error) {
      console.error('Error toggling visibility:', error);
      showError('Ошибка при изменении видимости объекта');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingCity(null);
    setFormData({ name: '', description: '', visible_in_schedules: true });
  };

  // Функция для определения цвета статуса (мягкие пастельные оттенки)
  const getStatusStyle = (status) => {
    const statusColors = {
      'Завершен': { bg: '#8bc49a', text: '#fff' },
      'Строительство': { bg: '#d4b896', text: '#fff' },
      'Проектирование': { bg: '#7b9eb8', text: '#fff' },
      'Согласование': { bg: '#a99bc4', text: '#fff' },
      'Подготовка': { bg: '#7ab5ad', text: '#fff' },
      'Поиск участка': { bg: '#8b9cc4', text: '#fff' },
      'Приобретение прав на участок': { bg: '#b8a0c4', text: '#fff' },
      'Есть право на зу, ждет реализации': { bg: '#d4a0b8', text: '#fff' },
      'Проектирование, экспертиза, получение РНС': { bg: '#7ab5ad', text: '#fff' },
    };

    return statusColors[status] || { bg: '#64748b', text: '#fff' };
  };

  return (
    <div className="container-fluid">
      <h1>Управление объектами строительства</h1>
      
      {citiesCanEdit && (
        <button 
          className="btn btn-primary" 
          onClick={() => setShowForm(!showForm)}
          style={{ marginBottom: '20px' }}
        >
          {showForm ? 'Отмена' : '+ Добавить объект строительства'}
        </button>
      )}
      {!citiesCanEdit && (
        <p style={{ color: 'var(--text-muted)', marginBottom: '20px' }}>
          Режим просмотра — редактирование объектов недоступно для вашей роли.
        </p>
      )}

      {showForm && citiesCanEdit && (
        <div className="card" style={{ maxWidth: '800px' }}>
          <h3>{editingCity ? 'Редактировать объект строительства' : 'Новый объект строительства'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Название объекта</label>
              <input
                type="text"
                className="form-control"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Описание</label>
              <textarea
                className="form-control"
                rows="3"
                placeholder="Введите описание объекта строительства"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.visible_in_schedules}
                  onChange={(e) => setFormData({ ...formData, visible_in_schedules: e.target.checked })}
                  style={{ cursor: 'pointer' }}
                />
                <span>📊 Отображать в графиках отделов</span>
              </label>
              <small style={{ color: 'var(--text-muted)', marginLeft: '28px', display: 'block', marginTop: '4px' }}>
                Когда отключено, объект не отображается в графиках отделов и в таблице
                Мастер-карты стратегического развития, но учитывается в блоке ИТОГО.
              </small>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={submitting}
                style={{ opacity: submitting ? 0.7 : 1 }}
              >
                {submitting ? '⏳ Сохранение...' : (editingCity ? 'Обновить' : 'Создать')}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={resetForm}
                disabled={submitting}
              >
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card-full-width">
        <h3>Список объектов строительства</h3>
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Название</th>
                <th>Описание</th>
                <th>Статус</th>
                <th>Видимость в графиках</th>
                <th>Дата создания</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {cities.map(city => (
                <tr key={city.id}>
                  <td>{city.id}</td>
                  <td>{city.name}</td>
                  <td>{city.description || '-'}</td>
                  <td>
                    {city.current_status ? (
                      <span
                        style={{
                          backgroundColor: getStatusStyle(city.current_status).bg,
                          color: getStatusStyle(city.current_status).text,
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          display: 'inline-block',
                          whiteSpace: 'nowrap'
                        }}
                        title="Статус из Мастер-карты"
                      >
                        {city.current_status}
                      </span>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: '13px' }}>—</span>
                    )}
                  </td>
                  <td>
                    <button
                      className="btn btn-sm"
                      disabled={!citiesCanEdit}
                      onClick={() => citiesCanEdit && toggleVisibility(city)}
                      style={{
                        backgroundColor: city.visible_in_schedules ? '#8bc49a' : '#d4a0a0',
                        color: 'white',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500',
                        transition: 'all 0.2s',
                        minWidth: '120px'
                      }}
                      title={city.visible_in_schedules ? 'Кликните, чтобы скрыть из графиков' : 'Кликните, чтобы показать в графиках'}
                    >
                      {city.visible_in_schedules ? '✓ Отображается' : '✕ Скрыт'}
                    </button>
                  </td>
                  <td>{formatDate(new Date(city.created_at))}</td>
                  <td>
                    {citiesCanEdit ? (
                      <>
                        <button 
                          className="btn btn-secondary btn-sm" 
                          onClick={() => handleEdit(city)}
                          style={{ marginRight: '5px' }}
                        >
                          ✏️ Редактировать
                        </button>
                        <button 
                          className="btn btn-danger btn-sm" 
                          onClick={() => handleDelete(city.id)}
                          disabled={deleting === city.id}
                          style={{ opacity: deleting === city.id ? 0.7 : 1 }}
                        >
                          {deleting === city.id ? '⏳ Удаление...' : '🗑️ Удалить'}
                        </button>
                      </>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Только просмотр</span>
                    )}
                  </td>
                </tr>
              ))}
              {cities.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>
                    Нет объектов строительства. Добавьте первый объект.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Cities;
